import { useReducer, useCallback, useEffect, useRef } from "react"
import dayjs from "dayjs"
import { calculateMinWidth } from "src/utils/table-render-functions"
import { DATE_FORMAT } from "src/utils/constants"

const initialState = {
    loading: false,
    rows: [],
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") =>
    [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)

        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)

        return order === "desc" ? dateB - dateA : dateA - dateB
    })

const getResponseResults = (response) => {
    const body = response?.body

    if (Array.isArray(body)) return body
    if (Array.isArray(body?.results)) return body.results
    if (Array.isArray(body?.data)) return body.data
    if (Array.isArray(body?.data?.results)) return body.data.results

    return []
}

const removeRowIdentifiers = (row = {}) => {
    const payload = { ...row }

    delete payload.id
    delete payload.ID

    return payload
}

export const useRatingEmissaoTable = (
    emissaoService,
    cetip,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cetip)

    useEffect(() => {
        if (loadedRef.current === cetip) return

        loadedRef.current = cetip

        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
    }, [cetip])

    const normalizeRows = useCallback((listaResultados = []) => {
        return ordenaPorDataReferencia(listaResultados, "desc").map(
            (obj, index) => ({
                ...obj,
                id: obj?.id ?? obj?.ID ?? index,
            })
        )
    }, [])

    const applyRows = useCallback(
        (listaResultados = []) => {
            const rows = normalizeRows(listaResultados)
            dispatch({ type: "SET_ROWS", payload: rows })
            return rows
        },
        [normalizeRows]
    )

    const loadTableRows = useCallback(async () => {
        if (!cetip || cetip.length <= 4) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CETIP não informado ou inválido.",
                    type: "warning",
                },
            })

            dispatch({ type: "SET_ROWS", payload: [] })

            return
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.ratingEmissao(
                "get",
                `?CETIP=${encodeURIComponent(cetip.toUpperCase())}`
            )

            const listaResultados = getResponseResults(response)

            if (!listaResultados.length) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })

                dispatch({ type: "SET_ROWS", payload: [] })

                return
            }

            dispatch({ type: "CLOSE_ALERT" })
            applyRows(listaResultados)
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Não há dados para o CETIP consultado.",
                    type: "warning",
                },
            })

            dispatch({ type: "SET_ROWS", payload: [] })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [cetip, emissaoService, applyRows])

    const addRow = useCallback(async () => {
        const lastItem = state.rows[0]

        const payload = lastItem
            ? removeRowIdentifiers(lastItem)
            : {
                  CETIP: dadosEmpresa?.CETIP || cetip,
                  CPFCNPJ:
                      dadosEmpresa?.CPF_CNPJ_Devedor ||
                      dadosEmpresa?.CPFCNPJ_Devedor ||
                      dadosEmpresa?.CPFCNPJ,
                  DataReferencia: dayjs().format(DATE_FORMAT),
                  Devedor: dadosEmpresa?.Devedor || "N/A",
                  Setor: dadosEmpresa?.Setor || "N/A",
                  SubSetor:
                      dadosEmpresa?.Subsetor ||
                      dadosEmpresa?.SubSetor ||
                      "N/A",
                  Fonte: "N/A",
                  Rating: "N/A",
              }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            await emissaoService.ratingEmissao("post", payload)
            await loadTableRows()
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetip, dadosEmpresa, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            const id = row?.ID ?? row?.id

            if (!id) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Registro sem ID para exclusão.",
                        type: "warning",
                    },
                })
                return
            }

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingEmissao("delete", id)
                await loadTableRows()
            } catch {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            const id = row?.ID ?? row?.id

            if (!id) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Registro sem ID para atualização.",
                        type: "warning",
                    },
                })

                return row
            }

            dispatch({ type: "SET_LOADING", payload: true })

            try {
                await emissaoService.ratingEmissao("patch", id, row)
                await loadTableRows()

                return row
            } catch (error) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })

                throw error
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    return {
        state: {
            ...state,
            minWidthAnaliseCredito: calculateMinWidth(
                state.rows,
                "AnaliseCreditoEmissao"
            ),
            minWidthObservacao: calculateMinWidth(state.rows, "Observacao"),
        },
        actions: {
            loadTableRows,
            loadTableRowsDevedor: loadTableRows,
            setAlert: (payload) => dispatch({ type: "SET_ALERT", payload }),
            closeAlert: () => dispatch({ type: "CLOSE_ALERT" }),
            addRow,
            deleteRow,
            updateRow,
            setRows: (rows) => applyRows(rows),
        },
    }
}